import { Modal as ModalModule, SubscriptionPlan, UI, Utils } from 'common';
import Session from 'common/src/code/utils/Session';
import UserRoleEnum from 'common/src/code/utils/UserRole.enum';
import React from 'react';
import { RoutePath } from '../utils/RoutePath';
import AddSeatsModal from './AddSeatsModal.component';
import ConfirmDeleteModal from './ConfirmDeleteModal.component';
import SeatsCounter from './SeatsCounter.component';
import UserActionType from './UserActionType.enum';
import UserFiltersModal from './UserFiltersModal.component';
import UsersUtils from './Utils';

const { UpgradePlanModal } = SubscriptionPlan;

const { StatusModal } = ModalModule;
const { WrappedToolbar, WrappedButtonType } = UI;
const { BillingPlans, withLayoutAndModalContexts } = Utils;

const DeleteUserErrorModal = ({ modalId, data }) => (
	<StatusModal
		modalId={modalId}
		header="Error"
		iconType="error"
		title={data.title}
		description={data.description}
		primaryButton={{ text: 'Close', type: 'OK' }}
	/>
);

const UsersToolbar = ({
	userID,
	handleSearchUsers,
	handleAddUser,
	handleExportToCsv,
	selectedUsers,
	action,
	handleDeleteUser,
	isEnterpriseAccount,
	enterpriseLocation,
	modalContext,
	handleFilter,
	locations,
	regions,
	filters,
	isEnableFilter,
	usedSeats,
	maxSeats,
	includedSeats,
	purchasedSeats,
	seatPrice,
	onUpgradeComplete,
	history,
	canManageAccountOwners,
}) => {
	const isAtSeatLimit =
		maxSeats !== null && maxSeats !== undefined && usedSeats >= maxSeats;
	const handleDelete = () => {
		const cannotDeleteUser =
			selectedUsers &&
			selectedUsers.find(user => user.UserId === userID) !== undefined;

		const cannotDeleteCorporateAdmin =
			Session.getUserRole() === UserRoleEnum.RegionAdmin &&
			selectedUsers.some(user => user.Role === UserRoleEnum.CorpAdmin);

		const cannotDeleteAccountOwner =
			!canManageAccountOwners &&
			selectedUsers.some(user => UsersUtils.isUserAccountOwner(user));

		if (cannotDeleteUser) {
			modalContext.showModal({
				component: DeleteUserErrorModal,
				data: {
					title: 'You cannot delete your own account.',
					description:
						'In order to have it removed, please ask another administrator for assistance.',
				},
			});
		} else if (cannotDeleteCorporateAdmin) {
			modalContext.showModal({
				component: DeleteUserErrorModal,
				data: {
					title: 'You cannot delete a user with a role above your own.',
				},
			});
		} else if (cannotDeleteAccountOwner) {
			modalContext.showModal({
				component: DeleteUserErrorModal,
				data: {
					title: 'You cannot delete an Account Owner.',
					description:
						'Only an Account Owner, Corporate Admin, or Region Admin can delete an Account Owner.',
				},
			});
		} else {
			modalContext.showModal({
				component: ConfirmDeleteModal,
				data: {
					selectedUsers,
					handleDeleteUser,
				},
			});
		}
	};

	const handleFilterUsers = () => {
		modalContext.showModal({
			component: UserFiltersModal,
			data: {
				locations,
				regions: [...regions, { Name: 'No Region' }],
				handleFilter,
				handleSearchUsers,
				filters,
			},
		});
	};

	const buttons = [
		{
			label: 'Add',
			icon: isAtSeatLimit ? 'upgrade-icon' : 'add-icon',
			iconPos: 'left',
			className: isAtSeatLimit
				? 'toolbar-icon-btn-upgrade'
				: 'toolbar-icon-btn',
			onClick: handleAddUser,
			disabled:
				!isAtSeatLimit &&
				((selectedUsers && selectedUsers.length > 0) ||
					action === UserActionType.Add),
			tooltip: isAtSeatLimit
				? `<b>Add</b>\nSeat limit reached`
				: `<b>Add</b>\nAdd a new user`,
			tooltipOptions: {
				position: 'right',
				className: 'p-tooltip-expand-width',
			},
		},
		{
			label: 'Delete',
			onClick: handleDelete,
			disabled:
				(selectedUsers && selectedUsers.length === 0) ||
				action === UserActionType.Add,
			className: 'toolbar-icon-btn',
			tooltip: `<b>Delete</b>\nDelete selected users`,
			tooltipOptions: {
				position: 'right',
				className: 'p-tooltip-expand-width',
			},
		},
		{
			icon: 'export-icon',
			buttonType: WrappedButtonType.TertiaryWhite,
			label: 'Export',
			group: 'right',
			hidden: true,
			onClick: handleExportToCsv,
			tooltip: `<b>Export</b>\nExport Users to a csv file.`,
			tooltipOptions: {
				position: 'bottom',
			},
		},
	];

	if (isEnterpriseAccount && !enterpriseLocation) {
		buttons.push({
			type: 'button',
			label: 'Filters',
			onClick: handleFilterUsers,
			disabled: !isEnableFilter,
			tooltip: `<b>Filter</b>\nFilter the users shown in the grid.`,
			tooltipOptions: {
				position: 'right',
				className: 'p-tooltip-expand-width',
			},
		});
	}

	const hasSeatsData =
		BillingPlans.isNewLineupPlan() &&
		maxSeats !== null &&
		maxSeats !== undefined;

	const handleAddSeats = () => {
		if (isAtSeatLimit) {
			modalContext.showModal({
				component: AddSeatsModal,
				data: {
					maxSeats,
					onNavigate: history?.push.bind(history),
				},
			});
		} else {
			history?.push(RoutePath.changePlan, { focusSeats: true });
		}
	};

	const handleUpgrade = () => {
		const upgradePlan = BillingPlans.getNextUpgradePlan();
		if (!upgradePlan) return;

		modalContext.showModal({
			component: UpgradePlanModal,
			data: {
				upgradePlan,
				triggerFeature: 'Users',
				onUpgradePlanCallback: onUpgradeComplete,
			},
		});
	};

	const seatsCounter = hasSeatsData ? (
		<SeatsCounter
			usedSeats={usedSeats}
			maxSeats={maxSeats}
			includedSeats={includedSeats}
			purchasedSeats={purchasedSeats}
			seatPrice={seatPrice}
			onAddSeats={handleAddSeats}
			onUpgrade={handleUpgrade}
		/>
	) : null;

	return (
		<div className="account-users-users-toolbar">
			<WrappedToolbar
				showSearch
				buttons={buttons}
				searchCallback={handleSearchUsers}
				searchInputPlaceholder="Search Users"
				dataqa="users-toolbar"
				extraContent={seatsCounter}
			/>
		</div>
	);
};

export default withLayoutAndModalContexts(UsersToolbar);
